import { motion } from 'framer-motion';
import React from 'react';
import { FiActivity, FiArrowRight, FiDollarSign, FiHeart, FiMonitor, FiServer, FiShoppingCart } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const Expertise = () => {
  const expertiseAreas = [
    {
      id: 'healthcare',
      title: 'Healthcare',
      subtitle: 'Primary Expertise',
      description: 'Revolutionizing patient care through innovative technology solutions, digital health platforms, and AI-powered diagnostics.',
      icon: FiHeart,
      gradient: 'from-blue-500 to-cyan-600',
      bgGradient: 'from-blue-50 to-cyan-50',
      features: [
        'Electronic Health Records (EHR) Integration',
        'Telemedicine & Remote Patient Monitoring Platforms',
        'Medical Device & Wearable Data Integration',
        'HIPAA-Compliant Cloud Infrastructure',
        'AI for Predictive Health Risk Analysis'
      ],
      stats: [
        { label: 'Healthcare Projects', value: '15+' },
        { label: 'Patient Records Managed', value: '20k+' },
        { label: 'Healthcare Providers', value: '50+' }
      ]
    },
    {
      id: 'life-sciences',
      title: 'Life Sciences',
      subtitle: 'Research & Development',
      description: 'Accelerating scientific discovery with advanced data analytics',
      icon: FiActivity,
      gradient: 'from-green-500 to-emerald-600',
      bgGradient: 'from-green-50 to-emerald-50',
      features: [
        'Clinical Trial Management Systems (CTMS)',
        'Laboratory Information Management Systems (LIMS)',
        'Regulatory Compliance Automation (21 CFR Part 11)',
        'Real-World Evidence (RWE) Data Platforms',
        'Drug Discovery & Genomic Data Integration'
      ],
      stats: [
        { label: 'Clinical Trials Supported', value: '55+' },
        { label: 'Research Institutions', value: '15+' },
        { label: 'Regulatory Submissions', value: '100+' }
      ]
    },
    {
      id: 'financial-services',
      title: 'Financial Services',
      subtitle: 'Digital Banking & Fintech',
      description: 'Transforming financial institutions with secure payment systems, risk management tools, and digital banking solutions.',
      icon: FiDollarSign,
      gradient: 'from-indigo-500 to-purple-600',
      bgGradient: 'from-indigo-50 to-purple-50',
      features: [
        'Digital Banking Platforms',
        'Payment Processing Systems',
        'Risk Management Solutions',
        'Regulatory Reporting',
        'Fraud Detection Systems'
      ],
      stats: [
        { label: 'Financial Institutions', value: '20+' },
        { label: 'Transactions Processed', value: '100k+' },
        { label: 'Compliance Projects', value: '70+' }
      ]
    },
    {
      id: 'technology',
      title: 'Technology',
      subtitle: 'Enterprise Solutions',
      description: 'Building scalable enterprise applications, cloud infrastructure, and cutting-edge software solutions for modern businesses.',
      icon: FiMonitor,
      gradient: 'from-orange-500 to-red-600',
      bgGradient: 'from-orange-50 to-red-50',
      features: [
        'Enterprise Software Development',
        'Cloud Infrastructure',
        'DevOps & Automation',
        'API Development',
        'System Integration'
      ],
      stats: [
        { label: 'Enterprise Clients', value: '80+' },
        { label: 'Applications Deployed', value: '50+' },
        { label: 'Cloud Migrations', value: '70+' }
      ]
    },
    {
      id: 'enterprise-solutions',
      title: 'Enterprise Solutions',
      subtitle: 'Enterprise Solutions',
      description: 'Cross-Industry Enablement,Delivering scalable software and AI-driven solutions to help large enterprises improve productivity, automate workflows, and scale fast.',
      icon: FiServer,
      gradient: 'from-green-500 to-emerald-600',
      bgGradient: 'from-green-50 to-emerald-50',
      features: [
        'Custom SaaS Product Development',
        'AI Agents for Business Process Automation',
        'Enterprise Data Platform Engineering',
        'Digital Twin & Simulation Modeling',
        'Change Management & Agile Transformation'
      ],
      stats: [
        { label: 'Enterprise Clients', value: '10+' },
        { label: 'Applications Deployed', value: '10+' },
        { label: 'Cloud Migrations', value: '10+' }
      ]
    },
    {
      id: 'e-commerce-retail',
      title: 'E-commerce & Retail',
      subtitle: 'E-commerce & Retail',
      description: 'Building scalable e-commerce platforms, mobile apps, and AI-driven retail solutions to enhance customer experiences and drive sales growth',
      icon: FiShoppingCart,
      gradient: 'from-yellow-500 to-orange-600',
      bgGradient: 'from-yellow-50 to-orange-50',
      features: [
        'E-commerce Platform Development',
        'Mobile App Development',
        'AI-Powered Recommendation Systems',
        'Inventory & Order Management',
        'AR/VR Shopping Experience Design'
      ],
      stats: [
        { label: 'Enterprise Clients', value: '10+' },
        { label: 'Applications Deployed', value: '10+' },
        { label: 'Cloud Migrations', value: '10+' }
      ]
    },
    
    
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide">
              Industry Expertise
            </span>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight font-display tracking-tight text-gray-900 mb-6">
              Our
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Domain Expertise
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Deep industry knowledge combined with cutting-edge technology solutions. 
              We understand your unique challenges and deliver tailored innovations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Expertise Areas Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {expertiseAreas.map((area, index) => (
              <motion.div
                key={area.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="group"
              >
                <Link to={`/expertise/${area.id}`}>
                  <div className={`bg-gradient-to-br ${area.bgGradient} p-8 rounded-3xl border border-white/50 hover:shadow-2xl transition-all duration-500 group-hover:scale-105 relative overflow-hidden`}>
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Content */}
                    <div className="relative z-10">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <div className="flex items-center space-x-3 mb-2">
                            <div className={`w-12 h-12 bg-gradient-to-br ${area.gradient} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                              <area.icon className="text-white" size={24} />
                            </div>
                            {area.subtitle && (
                              <span className="px-3 py-1 bg-white/60 text-gray-700 rounded-full text-xs font-medium font-accent tracking-wide">
                                {area.subtitle}
                              </span>
                            )}
                          </div>
                          <h3 className="text-3xl font-bold text-gray-900 mb-3 font-display tracking-tight">
                            {area.title}
                          </h3>
                        </div>
                        <motion.div
                          whileHover={{ x: 5 }}
                          className="text-gray-600 group-hover:text-gray-900"
                        >
                          <FiArrowRight size={24} />
                        </motion.div>
                      </div>

                      {/* Description */}
                      <p className="text-gray-700 mb-8 font-body leading-relaxed text-lg">
                        {area.description}
                      </p>

                      {/* Features */}
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 font-heading tracking-wide">Key Capabilities</h4>
                        <div className="grid grid-cols-1 gap-3">
                          {area.features.slice(0, 3).map((feature, idx) => (
                            <div key={idx} className="flex items-center space-x-3">
                              <div className={`w-2 h-2 bg-gradient-to-br ${area.gradient} rounded-full`}></div>
                              <span className="text-gray-700 font-body text-sm">{feature}</span>
                            </div>
                          ))}
                          {area.features.length > 3 && (
                            <div className="flex items-center space-x-3 text-gray-600">
                              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                              <span className="text-sm font-body">+{area.features.length - 3} more capabilities</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-4">
                        {area.stats.map((stat, idx) => (
                          <div key={idx} className="text-center">
                            <div className={`text-2xl font-bold bg-gradient-to-br ${area.gradient} bg-clip-text text-transparent font-display`}>
                              {stat.value}
                            </div>
                            <div className="text-xs text-gray-600 font-body">{stat.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Expertise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-display tracking-tight">
              Why Choose Our
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                Industry Expertise
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Our deep industry knowledge ensures solutions that understand your unique challenges and regulatory requirements.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Domain Knowledge",
                description: "Deep understanding of industry-specific challenges and opportunities",
                icon: "🎯"
              },
              {
                title: "Regulatory Compliance",
                description: "Expertise in industry regulations and compliance requirements",
                icon: "⚖️"
              },
              {
                title: "Best Practices",
                description: "Proven methodologies and industry best practices implementation",
                icon: "✨"
              },
              {
                title: "Specialized Teams",
                description: "Consultants with deep industry experience and certifications",
                icon: "👥"
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100"
              >
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 font-heading tracking-wide">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 font-body leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-display tracking-tight">
              Ready to Transform Your Industry?
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto font-body font-light tracking-wide leading-relaxed">
              Let's discuss how our industry expertise can accelerate your digital transformation and drive growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 font-accent tracking-wide"
                >
                  Start Your Project
                </motion.button>
              </Link>
              <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 font-accent tracking-wide"
              >
                Schedule Consultation
              </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Expertise; 